import { forwardRef, ReactElement, RefAttributes, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Select, SelectForwardRef, SelectInstance, SelectProps } from '@any-ui-react/select';

import { Option, SingleValue } from './components';

export type CustomerTagOption = {
  label: string;
  value: string;
  taggedCount?: number;
};

export type CustomerTagFilterValue = { name: string; taggedCount: number };

export type CustomerTagFilterProps = SelectProps<CustomerTagOption> & {
  fetchCustomerTags?: () => Promise<CustomerTagFilterValue[] | string[]>;
  withCounter?: boolean;
};

export type CustomerTagFilter = (
  props: CustomerTagFilterProps & RefAttributes<SelectInstance<CustomerTagOption>>
) => ReactElement;

const NoOptionsMessage = () => {
  const { t } = useTranslation('anychat');
  return (
    <div className="text-gray-7 py-2 text-center">
      {t('anychat.shared.customerTag.form.noTags')}
    </div>
  );
};

export function toCustomerTagOption(name: string): { label: string; value: string };
export function toCustomerTagOption(props: CustomerTagFilterValue): {
  label: string;
  value: string;
  taggedCount?: number;
};
export function toCustomerTagOption(value: string | CustomerTagFilterValue): CustomerTagOption {
  return typeof value === 'string'
    ? { label: value, value: value }
    : { label: value.name, value: value.name, taggedCount: value.taggedCount };
}

export const CustomerTagFilter = forwardRef(
  (
    {
      isClearable = true,
      isSearchable = true,
      components,
      fetchCustomerTags,
      asyncOptions,
      withCounter,
      ...rest
    }: CustomerTagFilterProps,
    ref: SelectForwardRef<CustomerTagOption>
  ) => {
    const loadCustomerTags = useCallback(async () => {
      if (fetchCustomerTags) {
        const tags = await fetchCustomerTags();
        return tags.map(toCustomerTagOption);
      }

      return [];
    }, [fetchCustomerTags]);

    return (
      <Select
        ref={ref}
        asyncOptions={fetchCustomerTags ? loadCustomerTags : asyncOptions}
        components={{
          NoOptionsMessage,
          Option: (props) => (
            <Option {...props} withCounter={withCounter} children={props.children} />
          ),
          SingleValue: (props) => (
            <SingleValue {...props} withCounter={withCounter} children={props.children} />
          ),
          ...components,
        }}
        isClearable={isClearable}
        isSearchable={isSearchable}
        {...rest}
      />
    );
  }
) as CustomerTagFilter;
